import { HeaderSearch as HeaderSearchV2 } from '@components/SearchV2/HeaderSearch';
import { HeaderSearch as HeaderSearchSsr } from '@components/SearchV2/ssr/HeaderSearch';
import { ExposureEvent } from '@lib/flags/amplitude/ExposureEvent';
import { useFlags } from '@lib/flags/amplitude/client';
import { FLAG_TREATMENT, Flags } from '@lib/flags/flagConstants';
import { usePathname } from 'next/navigation';
import { getWithoutLocale } from 'utils';

export const SearchWrapper = ({
  searchPlaceholder,
  searchCompletionHint,
  searchCompletionAccepted,
}: {
  searchPlaceholder: string;
  searchCompletionHint: string;
  searchCompletionAccepted: string;
}) => {
  const { flags } = useFlags();
  const pathname = usePathname() || '/';
  const pathnameWithoutLocale = getWithoutLocale(pathname);
  const isOnSearchPage =
    pathnameWithoutLocale === '/search' ||
    pathnameWithoutLocale === '/search-v2';

  // The header renders on every page, so this is where both arms first see
  // their variant — exposing only on /search would omit everyone who never
  // reaches results. Harmless for a flag the deployment doesn't return:
  // ExposureEvent fires nothing on an undefined value, and the helper dedupes
  // per assignment.
  const exposure = <ExposureEvent flagName={Flags.SEARCH_SSR} />;

  // The SSR header owns its own lightweight engine, so it needs no `standalone`
  // distinction: it always full-navigates to /search, where the query is seeded
  // server-side.
  //
  // The flag arrives already resolved. The header renders only under
  // AppProviders, which passes serverSideFlags, and FlagsProvider then serves a
  // static context rather than fetching client-side. Both arms therefore pick
  // their header on the first render, server and client alike: no visitor sees
  // one box replaced by the other, and a missing flag means the CSR header for
  // the whole page life — the default-off behavior, identical to today.
  if (flags[Flags.SEARCH_SSR]?.value === FLAG_TREATMENT) {
    return (
      <>
        {exposure}
        <HeaderSearchSsr
          placeholder={searchPlaceholder}
          completionHint={searchCompletionHint}
          completionAcceptedLabel={searchCompletionAccepted}
        />
      </>
    );
  }

  return (
    <>
      {exposure}
      <HeaderSearchV2
        placeholder={searchPlaceholder}
        standalone={!isOnSearchPage}
      />
    </>
  );
};
